import React, { useState, useEffect } from 'react'
import { FaSearch, FaFilter } from 'react-icons/fa'
import { useForm, SubmitHandler } from 'react-hook-form'
import Select, { SingleValue, StylesConfig, components } from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import ProductsList from './Products/ProductsList'
import { Discover } from './Discover'
import { useFetchCategories } from 'hooks/useFetchCategoriesList'
import { ScrollToTopButton } from 'ui/ScrollToTopButton'
import { useLocation } from 'react-router-dom'

interface IFormInputs {
  searchQuery: string
}

type CategoryOption = {
  value: string | number
  label: string | number
}

const AnimatedMenu = (props: any) => {
  return (
    <components.Menu {...props}>
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '0.75rem',
          boxShadow:
            '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
        }}
      >
        {props.children}
      </motion.div>
    </components.Menu>
  )
}

const customSelectStyles: StylesConfig<CategoryOption, false> = {
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    color: state.isSelected ? '#fff' : 'var(--text-primary)',
    backgroundColor: state.isSelected
      ? 'var(--accent-primary)'
      : state.isFocused
        ? 'var(--bg-tertiary)'
        : 'var(--bg-primary)',
    textAlign: 'right',
    padding: '10px 15px',
    ':active': {
      backgroundColor: 'var(--accent-secondary)',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--text-primary)',
    textAlign: 'right',
    marginRight: '4px',
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: '56px',
    padding: '0 4px',
    fontSize: '16px',
    border: '2px solid #CCCCCC',
    borderColor: state.isFocused
      ? 'var(--accent-primary)'
      : 'var(--input-border)',
    borderRadius: '0.75rem',
    background: 'var(--input-bg)',
    direction: 'rtl',
    textAlign: 'right',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(94, 129, 172, 0.15)' : 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'var(--accent-primary)',
    },
  }),
  menu: (provided) => ({
    ...provided,
    direction: 'rtl',
    textAlign: 'right',
    backgroundColor: 'transparent', // Transparent to let motion.div handle bg
    boxShadow: 'none', // Remove shadow from container
    marginTop: '8px',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  input: (provided) => ({
    ...provided,
    direction: 'rtl',
    textAlign: 'right',
    color: 'var(--text-primary)',
  }),
  placeholder: (provided) => ({
    ...provided,
    direction: 'rtl',
    textAlign: 'right',
    color: 'var(--text-tertiary)',
    marginRight: '4px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? 'var(--accent-primary)' : 'var(--text-tertiary)',
    padding: '8px',
    '&:hover': {
      color: 'var(--accent-primary)',
    },
  }),
}

export const MainPage: React.FC = () => {
  const [submittedQuery, setSubmittedQuery] = useState('')
  const { register, handleSubmit, watch, reset, setValue } =
    useForm<IFormInputs>()
  const { categories, isLoading } = useFetchCategories()
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const searchQuery = watch('searchQuery')
  const location = useLocation()

  // Check if user is returning from login with search query
  useEffect(() => {
    if (
      location.state?.from === 'product-request' &&
      location.state?.productName
    ) {
      const productName = location.state.productName
      setValue('searchQuery', productName)
      setSubmittedQuery(productName)
    }
  }, [location.state, setValue])

  const categoryOptions: CategoryOption[] = (categories || []).map((cat) => ({
    value: cat.id,
    label: `${cat.name} (${cat.product_count})`,
  }))

  useEffect(() => {
    if (searchQuery === '') {
      setSubmittedQuery('')
    }
  }, [searchQuery])

  const onSubmitSearch: SubmitHandler<IFormInputs> = async (data) => {
    setSubmittedQuery(data.searchQuery.trim())
  }

  const handleClearSearch = () => {
    setSubmittedQuery('')
    reset()
  }

  const handleCategoryChange = (option: SingleValue<CategoryOption>) => {
    setSelectedCategory((option?.value as string) ?? '')
  }

  return (
    <div className='container my-10'>
      {/* Enhanced Search & Category Container */}
      <div
        className='max-w-9xl mx-auto mt-12 mb-4 p-6 md:p-10 lg:p-8 rounded-3xl shadow-xl backdrop-blur-md transition-all duration-300'
        style={{
          backgroundColor: 'var(--card-bg)',
          boxShadow: '0 20px 40px -10px var(--card-shadow)',
          border: '1px solid var(--bg-tertiary)',
        }}
      >
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-8 items-end'>
          {/* Search Section */}
          <div className='lg:col-span-7'>
            <label
              htmlFor='search'
              className='flex items-center mb-4 text-lg font-bold'
              style={{ color: 'var(--text-primary)' }}
            >
              <FaSearch
                className='ml-3 text-xl'
                style={{ color: 'var(--accent-primary)' }}
              />
              جستجوی کتاب
            </label>
            <form onSubmit={handleSubmit(onSubmitSearch)}>
              <div className='relative group'>
                <input
                  placeholder='نام کتاب، نویسنده یا موضوع مورد نظر خود را جستجو کنید...'
                  className='w-full py-4 px-6 rounded-xl border-2 outline-none font-medium text-base transition-all duration-300'
                  type='text'
                  id='search'
                  {...register('searchQuery')}
                  style={{
                    borderColor: 'var(--input-border)',
                    backgroundColor: 'var(--input-bg)',
                    minHeight: '56px',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--accent-primary)'
                    e.target.style.boxShadow =
                      '0 0 0 3px rgba(94, 129, 172, 0.15)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--input-border)'
                    e.target.style.boxShadow = 'none'
                  }}
                />
                <button
                  type='submit'
                  className='absolute left-3 top-1/2 -translate-y-1/2 border-0 rounded-lg p-3 flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95'
                  style={{
                    backgroundColor: 'var(--accent-primary)',
                    color: 'white',
                    minWidth: '44px',
                    minHeight: '44px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      'var(--navbar-hover)'
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(94, 129, 172, 0.3)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      'var(--accent-primary)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <FaSearch className='text-lg' aria-label='search' />
                </button>
              </div>
            </form>
          </div>

          {/* Category Section */}
          <div className='lg:col-span-5'>
            <label
              className='flex items-center mb-4 text-lg font-bold'
              style={{ color: 'var(--text-primary)' }}
            >
              <FaFilter
                className='ml-3 text-xl'
                style={{ color: 'var(--accent-primary)' }}
              />
              دسته‌بندی کتاب‌ها
            </label>
            <Select
              placeholder='انتخاب دسته‌بندی...'
              isLoading={isLoading}
              isSearchable
              options={categoryOptions}
              onChange={handleCategoryChange}
              styles={customSelectStyles}
              isClearable
              menuPortalTarget={document.body}
              components={{ Menu: AnimatedMenu }}
              value={
                categoryOptions.find((opt) => opt.value === selectedCategory) ||
                null
              }
            />
          </div>
        </div>
      </div>

      <section className='hero-outer max-w-9xl mx-auto mt-0'>
        <div className='hero-image-container'>
          <picture>
            <source
              type='image/webp'
              srcSet='/assets/images/bookstory_hero_assets/bookstory_768w.webp 768w, /assets/images/bookstory_hero_assets/bookstory_1280w.webp 1280w, /assets/images/bookstory_hero_assets/bookstory_1536w.webp 1536w, /assets/images/bookstory_hero_assets/bookstory_1920w.webp 1920w'
              sizes='(max-width: 600px) 90vw, (max-width: 1200px) 100vw, 1456px'
            />
            <img
              src='/assets/images/bookstory_hero_assets/bookstory_1536w.jpg'
              alt='BookStory second-hand bookstore hero'
              loading='eager'
              decoding='sync'
              className='hero-image'
            />
          </picture>

          {/* Gradient Overlay */}
          <div className='hero-overlay'></div>

          {/* Hero Content */}
          <motion.div
            className='hero-content'
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          >
            {/* <motion.h1
              className='hero-title'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
            >
              BookStory
            </motion.h1> */}
            {/* <motion.p
              className='hero-subtitle'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.6 }}
            >
              هر کتاب یک داستان
            </motion.p> */}
            {/* <motion.p
              className='hero-description'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.8 }}
            >
              هر داستان یک زندگی دوباره
            </motion.p> */}
            <motion.button
              className='hero-cta-button'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 1 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const productsSection = document.querySelector('.hero-outer')
                if (productsSection) {
                  const offset =
                    productsSection.getBoundingClientRect().bottom +
                    window.scrollY +
                    50
                  window.scrollTo({ top: offset, behavior: 'smooth' })
                }
              }}
            >
              مشاهده کتاب‌ها
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <div
        className='max-w-9xl mx-auto mt-8 rounded-xl shadow-lg p-6'
        style={{
          background: `linear-gradient(to right, var(--gradient-from), var(--gradient-to))`,
        }}
      >
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
          <div
            className='flex items-center backdrop-blur-sm rounded-lg p-4 transition-all duration-300 hover:scale-105'
            style={{ backgroundColor: 'var(--feature-card-bg)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                'var(--feature-card-hover)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'var(--feature-card-bg)')
            }
          >
            <img
              alt='call center logo'
              src='/assets/icons/call-center.png'
              className='feature-icon w-10 h-10 md:w-12 md:h-12 flex-shrink-0'
            />
            <h3
              className='text-sm md:text-base font-medium mr-3 mt-3'
              style={{ color: 'var(--text-primary)' }}
            >
              پشتیبانی تلفنی
            </h3>
          </div>
          <div
            className='flex items-center backdrop-blur-sm rounded-lg p-4 transition-all duration-300 hover:scale-105'
            style={{ backgroundColor: 'var(--feature-card-bg)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                'var(--feature-card-hover)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'var(--feature-card-bg)')
            }
          >
            <img
              alt='guarantee logo'
              src='/assets/icons/guarantee.png'
              className='feature-icon w-10 h-10 md:w-12 md:h-12 flex-shrink-0'
            />
            <h3
              className='text-sm md:text-base font-medium mr-3 mt-3'
              style={{ color: 'var(--text-primary)' }}
            >
              تضمین کیفیت کتاب
            </h3>
          </div>
          <div
            className='flex items-center backdrop-blur-sm rounded-lg p-4 transition-all duration-300 hover:scale-105'
            style={{ backgroundColor: 'var(--feature-card-bg)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                'var(--feature-card-hover)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'var(--feature-card-bg)')
            }
          >
            <img
              alt='7 days money back logo'
              src='/assets/icons/7-days-money-back.png'
              className='feature-icon w-10 h-10 md:w-12 md:h-12 flex-shrink-0'
            />
            <h3
              className='text-sm md:text-base font-medium mr-3 mt-3'
              style={{ color: 'var(--text-primary)' }}
            >
              ضمانت بازگشت وجه
            </h3>
          </div>
          <div
            className='flex items-center backdrop-blur-sm rounded-lg p-4 transition-all duration-300 hover:scale-105'
            style={{ backgroundColor: 'var(--feature-card-bg)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                'var(--feature-card-hover)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'var(--feature-card-bg)')
            }
          >
            <img
              alt='buy book logo'
              src='/assets/icons/buy.png'
              className='feature-icon w-10 h-10 md:w-12 md:h-12 flex-shrink-0'
            />
            <h3
              className='text-sm md:text-base font-medium mr-3 mt-2'
              style={{ color: 'var(--text-primary)' }}
            >
              ترویج اقتصاد چرخشی
            </h3>
          </div>
        </div>
      </div>

      {submittedQuery && submittedQuery.length > 0 ? (
        <Discover
          query={submittedQuery}
          setQueried={setSubmittedQuery}
          onClearSearch={handleClearSearch}
          categoryId={selectedCategory}
        />
      ) : (
        <ProductsList categoryId={selectedCategory} />
      )}
      <ScrollToTopButton />
    </div>
  )
}
